import {
  DatabaseAuto,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IcommerceUser,
  IdataArrayResponse,
  IdeleteOneResponse,
  Iestimate,
  IestimateUpdateReturn,
  IfilterProps,
  TestimateRequest
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';

type TestimateProps = Iestimate<number, IcommerceUser>;

/**
 * Represents an estimate in the system.
 * This class provides methods to manage and track estimates,
 * which are preliminary cost calculations for goods or services.
 *
 * @extends DatabaseAuto - Provides common database operation methods
 * @template TestimateProps - The type definition for estimate properties
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link Estimate} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const estimate = await Estimate.getAll();
 *
 * const estimate = await Estimate.filterAll({
 *
 * });
 *
 * const estimate = await Estimate.bulkDelete({
 *
 * });
 *
 * const estimate = await Estimate.getOne('resourceId');
 *
 * const estimate = await Estimate.remove();
 *
 * ```
 */
export class Estimate extends DatabaseAuto {
  /** Base API endpoint for estimate operations */
  static readonly baseUrl = '/estimate';

  /** Unique reference ID for the estimate */
  readonly urId: TestimateProps['urId'];

  /** Current status of the estimate */
  readonly estimateStatus: TestimateProps['estimateStatus'];

  /** Unique number identifying the estimate */
  readonly estimateNumber: TestimateProps['estimateNumber'];

  /** Related invoice information */
  readonly invoiceRelated: TestimateProps['invoiceRelated'];

  /** Company associated with the estimate */
  readonly company: TestimateProps['company'];

  /** Optional expiration date of the estimate */
  readonly expirationDate?: TestimateProps['expirationDate'];

  /**
   * Creates a new instance of Estimate.
   * @param {TestimateProps} data - The estimate data
   * @throws {Error} If the data is invalid or missing required fields
   */
  constructor(data: TestimateProps) {
    super(data);

    this.estimateNumber = data.estimateNumber;
    this.urId = data.urId;
    this.estimateStatus = data.estimateStatus;
    this.invoiceRelated = data.invoiceRelated;
    this.company = data.company;
    if (data.expirationDate) {
      this.expirationDate = new Date(data.expirationDate);
    }
  }

  /**
   * Retrieves all estimates with pagination support.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: Estimate[] }>}
   * An object containing the total count and an array of estimates
   * @throws {Error} If there's an error fetching the estimates
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<TestimateProps>>(`${Estimate.baseUrl}?${query}`);
    const estimates = await lastValueFrom(observer$);

    return {
      count: estimates.count,
      dataArr: estimates.data.map(val => new Estimate(val))
    };
  }

  /**
   * Filters estimates based on specified criteria.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: Estimate[] }>}
   * An object containing the total count and an array of filtered estimates
   * @throws {Error} If there's an error applying the filter
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TestimateProps>>(
        `${Estimate.baseUrl}/filter`,
        filter
      );
    const estimates = await lastValueFrom(observer$);

    return {
      count: estimates.count,
      dataArr: estimates.data.map(val => new Estimate(val))
    };
  }

  /**
   * Retrieves a single estimate by its ID.
   * @param {string} resourceId - The unique identifier of the estimate to retrieve
   * @returns {Promise<Estimate>} The requested estimate
   * @throws {Error} If the estimate is not found or an error occurs during retrieval
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<TestimateProps>(`${Estimate.baseUrl}/${resourceId}`);
    const estimate = await lastValueFrom(observer$);

    return new Estimate(estimate);
  }

  /**
   * Creates a new estimate record.
   * @param {TestimateRequest<true>} payload - The estimate data to create
   * @returns {Promise<Estimate>} The newly created estimate
   * @throws {Error} If the creation fails
   */
  static async add(payload: TestimateRequest<true>) {
    const observer$ = CommerceClient.ehttp
      .makePost<TestimateProps>(`${Estimate.baseUrl}`, payload);

    return new Estimate(await lastValueFrom(observer$));
  }

  /**
   * Deletes multiple estimates based on the provided criteria.
   * @param {IbulkDelete} payload - The criteria for identifying which estimates to delete
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the bulk delete operation
   * @throws {Error} If the bulk delete operation fails
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(
        `${Estimate.baseUrl}/bulk-delete`,
        payload
      );

    return lastValueFrom(observer$);
  }

  /**
   * Updates the current estimate with new values.
   * @param {TestimateRequest} payload - The data to update the estimate with
   * @returns {Promise<Estimate>} The updated estimate
   * @throws {Error} If the update operation fails
   */
  async update(payload: TestimateRequest) {
    const observer$ = CommerceClient.ehttp
      .makePut<IestimateUpdateReturn>(
        `${Estimate.baseUrl}/${this._id}`,
        payload
      );

    const res = await lastValueFrom(observer$);

    return {
      ...res,
      estimate: new Estimate(res.data.estimate)
    };
  }

  /**
   * Removes the current estimate from the system.
   * @returns {Promise<IdeleteOneResponse | null>} The result of the delete operation
   * @throws {Error} If the deletion fails or the estimate doesn't exist
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${Estimate.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
